import React from 'react';
import type { Editor } from 'ckeditor5';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CommonModal } from '../../../../../../../global/modal/common/common-modal';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../../../../global/rich-text-editor';
import DefaultButton from '../../../../../../../global/default-button/default-button';
import { ModuleDropdown } from '../_events-module/module-dropdown';
import { SpeakersDisplayFields } from './speakers-display-fields';
import { AddSpeakersPopoverButton } from './_add-speakers/_add-speaker-popover-button/add-speakers-popover-button';
import { SpeakerFormModal } from './_add-speakers/speaker-form-modal';
import { SpeakersList } from './_speaker-list/speakers-list';
import { ADD_TEXT, APPLY_TEXT, CANCEL_TEXT, DESCRIPTION as DESCRIPTION_LABEL_TEXT, } from '../../../../../../../../constants/common-labels.constants';
import { EnablementStatus } from '../../../../../../../../constants/common.constants';
import {
    EDIT_SPEAKERS_MODULE_MODAL_TITLE_TEXT,
    MODULE_ACTIVE_LABEL_TEXT,
    MODULE_LABEL_DISPLAYED_LABEL_TEXT,
    MODULE_LABEL_FIELD_LABEL_TEXT,
    MODULE_LAYOUT_FIELD_LABEL_TEXT,
    NEW_SPEAKERS_MODULE_MODAL_TITLE_TEXT,
    SPEAKERS_SECTION_LABEL_TEXT,
} from '../../../../../../event-hubs.text';
import { HubModuleLayout } from '../../../../../../../../api/event-hubs.dao';
import { SpeakersModuleVM } from './speakers-module.viewmodel';
import { DESCRIPTION, DISPLAY_FIELD_CONFIGS, DISPLAY_LABEL, LABEL, LAYOUT, LAYOUT_OPTIONS, } from './speakers-module.config';
import { FormFieldInput } from '../../../../../../../global/form/form-field-input/form-field-input';
import './speakers-module-modal.scss';

type SpeakersModuleModalProps = {
    vm: SpeakersModuleVM;
    isNew: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

export const SpeakersModuleModal: React.FC<SpeakersModuleModalProps> = observer(props => {
    const { vm, isNew, onCancel, onConfirm } = props;
    const { formatMessage } = useIntl();

    const labelDisplayedField = vm.getFormVmValuePropsByKey(DISPLAY_LABEL);
    const descriptionField = vm.getFormVmValuePropsByKey(DESCRIPTION);
    const layoutField = vm.getFormVmValuePropsByKey(LAYOUT);

    const titleDescriptor = isNew ? NEW_SPEAKERS_MODULE_MODAL_TITLE_TEXT : EDIT_SPEAKERS_MODULE_MODAL_TITLE_TEXT;
    const confirmDescriptor = isNew ? ADD_TEXT : APPLY_TEXT;

    const layoutOptions = LAYOUT_OPTIONS.map(({ value, labelText }) => ({
        value: value,
        label: formatMessage(labelText),
    }));

    const displayFields = DISPLAY_FIELD_CONFIGS.map(({ key, labelText }) => ({
        key: key,
        labelText: labelText,
        field: vm.getFormVmValuePropsByKey(key),
    }));

    const handleActiveToggle = () => vm.setEnabled(!vm.isEnabled());
    const handleLabelDisplayedToggle = () => labelDisplayedField.setValue(!labelDisplayedField.value);
    const handleDescriptionChange = (_: unknown, editor: Editor) => descriptionField.setValue(editor.getData());
    const handleLayoutChange = (val: HubModuleLayout) => layoutField.setValue(val);
    const hasDescriptionError = !descriptionField.valid && descriptionField.touched;

    return (
        <>
        <CommonModal
            isOpen
            title={<FormattedMessage {...titleDescriptor} />}
            onRequestClose={onCancel}
            className='speakers-module-modal'
            footer={
                <div className='speakers-module-modal__footer'>
                    <DefaultButton
                        modifier='cancel'
                        text={<FormattedMessage {...CANCEL_TEXT} />}
                        onClick={onCancel}
                    />
                    <DefaultButton
                        text={<FormattedMessage {...confirmDescriptor} />}
                        onClick={onConfirm}
                    />
                </div>
            }
        >
            <div className='speakers-module-modal__body'>
                <FormFieldInput.Toggle
                    isSelected={vm.isEnabled()}
                    toggle={handleActiveToggle}
                    text={<FormattedMessage
                        values={{ suffix: vm.isEnabled() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED }}
                        {...MODULE_ACTIVE_LABEL_TEXT}
                    />}
                    className='speakers-module-modal__toggle-row'
                />

                <div className='speakers-module-modal__label-row'>
                    <FormFieldInput.Text
                        field={vm.getFormVmValuePropsByKey(LABEL)}
                        labelDescriptor={MODULE_LABEL_FIELD_LABEL_TEXT}
                        required
                        additionalClassName='speakers-module-modal__label-input'
                        withError
                    />

                    <FormFieldInput.Toggle
                        isSelected={labelDisplayedField.value as boolean}
                        toggle={handleLabelDisplayedToggle}
                        text={<FormattedMessage
                            values={{ suffix: labelDisplayedField.value ? EnablementStatus.DISPLAYED : EnablementStatus.NOT_DISPLAYED }}
                            {...MODULE_LABEL_DISPLAYED_LABEL_TEXT}
                        />}
                        className='speakers-module-modal__toggle-row speakers-module-modal__toggle-row--inline'
                    />
                </div>

                <FormField
                    className='m-bottom-20px'
                    label={<FormattedMessage {...DESCRIPTION_LABEL_TEXT} />}
                >
                    <RichTextEditor
                        className='oe-form-field__control m-0px p-0px'
                        value={descriptionField.value as string || ''}
                        onChange={handleDescriptionChange}
                        onBlur={descriptionField.setTouched}
                        validationError={
                            hasDescriptionError &&
                            <FormattedMessage
                                values={{ fieldName: formatMessage(DESCRIPTION_LABEL_TEXT) }}
                                {...descriptionField.validationMessage}
                            />
                        }
                    />
                </FormField>

                <ModuleDropdown<HubModuleLayout>
                    label={<FormattedMessage {...MODULE_LAYOUT_FIELD_LABEL_TEXT} />}
                    value={layoutField.value as HubModuleLayout}
                    options={layoutOptions}
                    onChange={handleLayoutChange}
                    className='speakers-module-modal__layout-field m-bottom-20px'
                />

                <div className='speakers-module-modal__speakers-section'>
                    <span className='oe-form-field__title'>
                        <FormattedMessage {...SPEAKERS_SECTION_LABEL_TEXT} />
                    </span>
                    <SpeakersList speakers={vm.speakers} onRemove={vm.removeSpeaker} onEdit={vm.editSpeaker} />
                    <AddSpeakersPopoverButton
                        contacts={vm.contacts}
                        speakers={vm.speakers}
                        isLoading={vm.isLoadingContacts}
                        onOpen={vm.loadContacts}
                        resetLoadingState={vm.resetLoadingState}
                        onAddSpeaker={vm.openAddSpeakerModal}
                        onAddContacts={vm.addSpeakers}
                    />
                </div>

                <SpeakersDisplayFields fields={displayFields} />
            </div>
        </CommonModal>

        {vm.isAddSpeakerModalOpen &&
            <SpeakerFormModal
                accountId={vm.getAccountId()}
                speaker={vm.editingSpeaker}
                onCancel={vm.closeAddSpeakerModal}
                onConfirm={vm.editingSpeaker ? vm.confirmEditSpeaker : vm.confirmNewSpeaker}
            />}
        </>
    );
});
